import React, { useEffect, useState } from "react";
import * as ReactDOM from "react-dom/client";
import * as mortgageMethods from './mortgageMethods';
import './styles.scss';
const currencyFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatDollars(dollars) {
    return currencyFormatter.format(dollars);
}
const dateFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });
function formatDate(date) {
    return dateFormatter.format(date);
}
function NumericInput(props) {
    return React.createElement("div", null,
        React.createElement("label", null, props.label),
        React.createElement("input", { type: "number", value: props.value, min: props.min, max: props.max, step: props.step, onChange: e => props.setter(+e.target.value) }));
}
function TemplateTextDiv(props) {
    const decomposed = props.template.split(/\?(?!\?)/);
    if (decomposed.length !== props.displayItems.length + 1) {
        throw Error(`Cannot process template item. Expected ${decomposed.length} parameters.`);
    }
    const nodes = new Array();
    for (const [index, text] of decomposed.entries()) {
        nodes.push(React.createElement("span", { key: `text${index}` }, text));
        nodes.push(React.createElement("strong", { key: `index${index}` }, props.displayItems[index]));
    }
    return React.createElement("div", null, nodes.slice(0, -1));
}
function HelpPopOver(props) {
    const trueX = Math.min(window.innerWidth - 100, props.pos.x);
    return React.createElement("div", { className: "help-popover", style: { left: trueX, top: props.pos.y }, onClick: props.onDelete }, props.text);
}
function AggregateItem(props) {
    const [showHelp, setShowHelp] = useState(false);
    const [helpPos, setHelpPos] = useState({ x: 0, y: 0 });
    return React.createElement("span", { className: "aggregate-item" },
        React.createElement("h3", { className: "aggregate-title", title: props.help != null ? "Click to Show Help" : "", onClick: e => {
                setShowHelp(!showHelp);
                setHelpPos({ x: e.clientX, y: e.clientY });
            } },
            props.title,
            (showHelp && props.help)
                && React.createElement(HelpPopOver, { text: props.help, pos: helpPos, onDelete: _ => setShowHelp(false) })),
        React.createElement(TemplateTextDiv, { template: props.template, displayItems: props.displayItems }));
}
function DataDisplay(props) {
    var _a, _b;
    const [isShowingGraph, setIsShowingGraph] = useState(true);
    const effectivePrincipal = props.principal - props.initialPayment;
    const compoundRate = props.loanPercent / 100 / props.annualPaymentCycles;
    const parameterValidators = [
        [props.annualPaymentCycles > 0, "Annual Payments is not > 0"],
        [props.loanPercent >= 0, "Loan Percent is not >= 0"],
        [props.principal > 0, "Principal is not > 0"],
        [props.initialPayment >= 0, "Initial Payment is not >= 0"],
        [props.paymentPerCycle > 0, "Payment Per Cycle is not > 0"],
        [effectivePrincipal > 0, "Trivial solution: only 1 payment required."]
    ];
    const failedParams = parameterValidators.filter(p => !p[0]);
    function getPaymentDataForPrincipal(principal) {
        if (principal === 0)
            return [];
        try {
            return mortgageMethods.getPaymentData(principal, compoundRate, props.paymentPerCycle, props.escrowAdjustment, props.annualPaymentCycles);
        }
        catch (e) {
            return [];
        }
    }
    // Real payment
    const realPaymentData = getPaymentDataForPrincipal(effectivePrincipal);
    const totalInterestPaid = realPaymentData.reduce((prev, cur) => prev + cur.interest, 0);
    const interestEfficiency = 1 - totalInterestPaid / props.principal;
    const finalPaymentDate = (_b = (_a = realPaymentData.slice(-1)[0]) === null || _a === void 0 ? void 0 : _a.timeStamp) !== null && _b !== void 0 ? _b : new Date(Date.now());
    // Speculative payment
    let initialPaymentEffect;
    try {
        const noInitialPaymentData = getPaymentDataForPrincipal(props.principal);
        const interestWithoutInitial = noInitialPaymentData.reduce((prev, cur) => prev + cur.interest, 0);
        initialPaymentEffect = (interestWithoutInitial - totalInterestPaid) / props.principal;
    }
    catch (_) {
        initialPaymentEffect = NaN;
    }
    const totalPayments = realPaymentData.length - 1;
    const intBreakEvenCycles = Math.ceil(totalInterestPaid / props.rentPayment);
    const totalPayment = totalInterestPaid + props.principal;
    const totalBreakEvenCycles = Math.ceil(totalPayment / props.rentPayment);
    useEffect(() => {
        if (realPaymentData.length === 0)
            return;
        if (!isShowingGraph)
            return;
        const canvas = document.getElementById('data-graph');
        if (canvas == null) {
            console.error('Cannot get canvas');
            return;
        }
        const ctx = canvas.getContext('2d');
        if (ctx == null) {
            console.error('Cannot get 2d context');
            return;
        }
        const width = canvas.width;
        const height = canvas.height;
        function mapFunc(dataX, dataY, maxX, maxY) {
            return [
                dataX / maxX * width,
                (1 - dataY / maxY) * height
            ].map(Math.floor);
        }
        function graphProperty(data, xValPredicate, yValPredicate, maxXData, maxYData, color) {
            if (ctx == null) {
                console.error('Cannot get 2d context');
                return;
            }
            ctx.beginPath();
            ctx.strokeStyle = color;
            const [firstX, firstY] = mapFunc(0, yValPredicate(data[0]), maxXData, maxYData);
            ctx.moveTo(firstX, firstY);
            for (const entry of data.slice(1)) {
                const [curX, curY] = mapFunc(xValPredicate(entry), yValPredicate(entry), maxXData, maxYData);
                ctx.lineTo(curX, curY);
            }
            ctx.stroke();
        }
        ctx.clearRect(0, 0, width, height);
        const now = Date.now();
        graphProperty(realPaymentData, val => val.timeStamp.getTime() - now, val => val.remainingBalance, finalPaymentDate.getTime() - Date.now(), effectivePrincipal, 'black');
        graphProperty(realPaymentData, val => val.timeStamp.getTime() - now, val => val.principal / (val.principal + val.interest), finalPaymentDate.getTime() - Date.now(), 1, 'blue');
    }, [realPaymentData, isShowingGraph]);
    if (failedParams.length > 0) {
        return React.createElement("ul", null, failedParams.map(fp => React.createElement("li", { key: fp[1] }, fp[1])));
    }
    return React.createElement("div", { id: "data-display" },
        React.createElement("header", null,
            React.createElement(AggregateItem, { title: "Loan Maturity", help: "How soon you pay it off", template: "?, in ? cycles or ? years", displayItems: [
                    finalPaymentDate.toDateString(),
                    totalPayments,
                    (totalPayments / props.annualPaymentCycles).toFixed(2)
                ] }),
            React.createElement(AggregateItem, { title: "Total Payment", help: "Principal + Interest", template: "?", displayItems: [formatDollars(totalInterestPaid + props.principal)] }),
            React.createElement(AggregateItem, { title: "Total Interest", help: "How much interest is paid when the loan matures", template: "?", displayItems: [formatDollars(totalInterestPaid)] }),
            React.createElement(AggregateItem, { title: "Interest Efficiency", help: "1 - (Interest paid / original principal)", template: "?%", displayItems: [(100 * interestEfficiency).toFixed(2)] }),
            React.createElement(AggregateItem, { title: "Initial Payment Effect", help: "How much the initial payment contributes to interest efficiency", template: "?%", displayItems: [(100 * initialPaymentEffect).toFixed(2)] })),
        React.createElement("header", null,
            props.rentPayment > 0
                && React.createElement(AggregateItem, { title: "Rent-interest break-even point", help: "How long until rent pays for interest", template: "? cycles, or ? years", displayItems: [
                        intBreakEvenCycles,
                        (intBreakEvenCycles / props.annualPaymentCycles).toFixed(2)
                    ] }),
            props.rentPayment > 0
                && React.createElement(AggregateItem, { title: "Rent-total break-even point", help: "How long until rent pays for entire loan", template: "? cycles, or ? years", displayItems: [
                        totalBreakEvenCycles,
                        (totalBreakEvenCycles / props.annualPaymentCycles).toFixed(2)
                    ] })),
        React.createElement("div", { id: "display-settings" },
            React.createElement("input", { type: "button", value: "Show/Hide Graph", onClick: _ => setIsShowingGraph(!isShowingGraph) }),
            React.createElement("div", { id: "legend" },
                React.createElement("strong", null, "Key"),
                React.createElement("div", { id: "keys" },
                    React.createElement("strong", { style: { color: 'blue' } }, "Principal:Payment Ratio"),
                    React.createElement("strong", { style: { color: 'black' } }, "Remaining Balance")))),
        isShowingGraph
            && React.createElement("canvas", { id: "data-graph", width: 600, height: 300 }),
        React.createElement("div", { id: "data-display-container" },
            React.createElement("table", { id: "data-grid" },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("td", null, "Cycle"),
                        React.createElement("td", null, "Date"),
                        React.createElement("td", null, "Payment"),
                        React.createElement("td", null, "Principal"),
                        React.createElement("td", null, "\u0394 Principal"),
                        React.createElement("td", null, "Interest"),
                        React.createElement("td", null, "\u0394 Interest"),
                        React.createElement("td", null, "Escrow/other"),
                        React.createElement("td", null, "Remaining Principal"),
                        React.createElement("td", null, "\u0394 R. Principal"))),
                React.createElement("tbody", null, realPaymentData.map((payment, idx) => (React.createElement("tr", { key: payment.timeStamp.toString() },
                    React.createElement("td", null, idx + 1),
                    React.createElement("td", null, formatDate(payment.timeStamp)),
                    React.createElement("td", null, formatDollars(payment.totalAmount)),
                    React.createElement("td", null, formatDollars(payment.principal)),
                    React.createElement("td", null, formatDollars(payment.dPrincipal)),
                    React.createElement("td", null, formatDollars(payment.interest)),
                    React.createElement("td", null, formatDollars(payment.dInterest)),
                    React.createElement("td", null, formatDollars(props.escrowAdjustment)),
                    React.createElement("td", null, formatDollars(payment.remainingBalance)),
                    React.createElement("td", null, formatDollars(payment.dRemainingBalance)))))))));
}
function AppRoot() {
    // Required
    const [annualPaymentCycles, setAnnualPaymentCycles] = useState(12);
    const [loanPercent, setLoanPercent] = useState(5);
    const [principal, setPrincipal] = useState(100000);
    const [initialPayment, setInitialPayment] = useState(0);
    const [paymentPerCycle, setPaymentPerCycle] = useState(1000);
    const [escrowAdjustment, setEscrowAdjustment] = useState(200);
    // Others
    const [rentPayment, setRentPayment] = useState(0);
    return React.createElement(React.Fragment, null,
        React.createElement("div", { id: "input-fields" },
            React.createElement("div", { className: "input-set" },
                React.createElement("h2", null, "Required inputs"),
                React.createElement(NumericInput, { label: "# Annual Payments", value: annualPaymentCycles, min: 0, setter: setAnnualPaymentCycles }),
                React.createElement(NumericInput, { label: "Loan Rate (%)", value: loanPercent, min: 0, setter: setLoanPercent }),
                React.createElement(TemplateTextDiv, { template: "-- (Per cycle: ?%)", displayItems: [(loanPercent / annualPaymentCycles).toFixed(4)] }),
                React.createElement(NumericInput, { label: "Starting Principal", value: principal, min: 0, step: 1000, setter: setPrincipal }),
                React.createElement(NumericInput, { label: "Initial Payment", value: initialPayment, min: 0, step: 1000, setter: setInitialPayment }),
                React.createElement(NumericInput, { label: "Payment per cycle", value: paymentPerCycle, min: 0, step: 100, setter: setPaymentPerCycle }),
                React.createElement(NumericInput, { label: "Escrow Adjustment (per cycle)", value: escrowAdjustment, min: 0, step: 10, setter: setEscrowAdjustment })),
            React.createElement("div", { className: "input-set" },
                React.createElement("h2", null, "Other inputs"),
                React.createElement(NumericInput, { label: "Rent per cycle", value: rentPayment, min: 0, step: 100, setter: setRentPayment }))),
        React.createElement("hr", null),
        React.createElement(DataDisplay, { annualPaymentCycles: annualPaymentCycles, loanPercent: loanPercent, principal: principal, initialPayment: initialPayment, paymentPerCycle: paymentPerCycle, escrowAdjustment: escrowAdjustment, rentPayment: rentPayment }));
}
document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('app-root-container');
    if (container == null)
        throw 'Bad id probably';
    const appRoot = ReactDOM.createRoot(container);
    appRoot.render(React.createElement(AppRoot, null));
});
